import { useTranslation } from "react-i18next";
import { Box, Button, Stack, Table, TableBody, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { useConsultContext } from "./ConsultContext";
import i18n from "../../i18n";
import FormatHelpers from "../../services/FormatHelper";
import { useState } from "react";
import { Notify } from "../../services/Notify";
import consultService from "../../services/Consults/ConsultService";
import LoadingFullScreen from "../../components/LoadingFullScreen";
import PaymentStateComponent from "../../components/Payments/PaymentStateComponent";
import { PaymentState } from "../../services/Payments/PaymentState";
import ConsultHelpers from "../../services/Consults/ConsultHelpers";

export default function ConsultTabAdmin() {
  const { t } = useTranslation();

  const { consult, refresh } = useConsultContext();

  const [working, setWorking] = useState<boolean>(false);
  const [inAssignUser, setInAssignUser] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>("");

  const payment = consult?.payment;
  const paymentLink = payment ? `https://dashboard.stripe.com/payments/${payment.externalId}` : "";

  const assignEmail = async () => {
    try {
      setWorking(true);
      await consultService.changeConsultEmail(consult!.organizationId, consult!.id, newEmail);
      await refresh();
      Notify.success(`${t("consult.change-user-submit-ok")}`);
    } catch (e) {
      console.error("consult.submit change user email", e);
      Notify.error(`${t("consult.change-user-submit-error")}`);
    } finally {
      setWorking(false);
    }
  };

  const refundConsult = () => {
    if (consult) {
      consultService
        .refundConsult(consult?.organizationId, consult.id)
        .then(refresh)
        .catch(() => Notify.error(`${t("common.unexpected-error")}`));
    }
  };

  if (working) {
    return <LoadingFullScreen center={true} />;
  }

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell colSpan={2} sx={{ fontWeight: "bold", fontSize: "1.1em" }}>
            {t("consult.patient-informations")}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>{t("common.email")}</TableCell>
          {consult?.patientEmail ? (
            <TableCell>{consult.patientEmail}</TableCell>
          ) : (
            <TableCell>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography>None</Typography>

                <Box flex={1}>
                  {inAssignUser ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>{t("common.email")}</Typography>
                      <TextField sx={{ flex: 1 }} size="small" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                      <Button variant="outlined" disabled={newEmail.length < 3} onClick={assignEmail}>
                        {t("consult.change-user-assign")}
                      </Button>
                    </Stack>
                  ) : (
                    <Button variant="outlined" onClick={() => setInAssignUser(true)}>
                      {t("consult.change-user")}
                    </Button>
                  )}
                </Box>
              </Stack>
            </TableCell>
          )}
        </TableRow>

        <TableRow>
          <TableCell colSpan={2} sx={{ fontWeight: "bold", fontSize: "1.1em" }}>
            {t("consult.payment")}
          </TableCell>
        </TableRow>

        {payment ? (
          <>
            <TableRow>
              <TableCell>Id</TableCell>
              <TableCell>{payment.id}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>State</TableCell>
              <TableCell>{t(`payment-state.${payment.state}`)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Card</TableCell>
              <TableCell>
                {payment.cardBrand} {payment.cardLast4}, exp. {payment.cardExpiration}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell>{FormatHelpers.formatMoney(payment.amount, i18n.language)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>External Payment Id</TableCell>
              <TableCell>
                <a target="_blank" href={paymentLink}>
                  {payment.externalId}
                </a>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>External Payment Token</TableCell>
              <TableCell>{payment.externalToken}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell></TableCell>
              <TableCell>
                {consult.payment?.state && consult?.payment?.state === PaymentState.Refunded ? (
                  <PaymentStateComponent state={consult.payment?.state} />
                ) : (
                  <>
                    {ConsultHelpers.isConsultActive(consult) && (
                      <Button variant="outlined" sx={{ ml: 1 }} onClick={refundConsult}>
                        <Typography fontWeight="bold">{t("consult.refund")}</Typography>
                      </Button>
                    )}
                  </>
                )}
              </TableCell>
            </TableRow>
          </>
        ) : (
          <TableRow>
            <TableCell colSpan={2}>No payment.</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
